import { useNotification } from '@/containers'
import type { FlashSale, Product, PromotionProductCarousel } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { mergeArrays } from '@/utils/next-util'
import uniqueId from 'lodash/uniqueId'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Autoplay, FreeMode, Grid, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import FlashSaleProductCountdown from '../FlashSaleProductCountdown'
import ProductCard from '../ProductCard'

const NUMBER_LINE_PRODUCT = 2

interface ProductCarouselProps {
  data: PromotionProductCarousel | FlashSale
  headerClassName?: string
  hideViewAll?: boolean
  showCountdown?: boolean
}

const ProductCarousel = ({
  data,
  headerClassName = 'min-h-[90px]',
  hideViewAll = false,
  showCountdown = false,
}: ProductCarouselProps) => {
  const { systemConfig } = useNotification()
  const qtt =
    systemConfig?.imageQlt && systemConfig?.imageQlt != ''
      ? systemConfig?.imageQlt
      : 80

  const customStyle = {
    backgroundColor: data?.customization?.backgroundColor,
    backgroundImage:
      data?.customization?.backgroundImage &&
      `url(${data?.customization?.backgroundImage}?format=webp&quality=${qtt})`,
    backgroundRepeat: data?.customization?.backgroundImage && 'no-repeat',
    backgroundPosition: data?.customization?.backgroundImage && 'center center',
    backgroundSize: data?.customization?.backgroundImage && '100% 100%',
  }
  const arrDataPC: Product[] =
    data?.products?.map((e, i) => {
      return { ...e, index: i + 1 }
    }) || []

  const [itemsPerRows, setItemsPerRows] = useState(5)

  const rows = useMemo(() => {
    if (data?.numberLineProduct) {
      return data.numberLineProduct
    }

    let rows = NUMBER_LINE_PRODUCT
    const numberOfItems = arrDataPC.length || 0
    while (rows * itemsPerRows > numberOfItems) {
      rows = rows - 1
    }

    return rows || 1
  }, [arrDataPC.length, data?.numberLineProduct, itemsPerRows])

  const sortDataPC = useMemo(() => {
    return mergeArrays(arrDataPC, rows)
  }, [arrDataPC, rows])

  const buttonSeq = useRef(uniqueId())
  const nextBtnClassName = `swpBtnNext${buttonSeq.current}`
  const prevBtnClassName = `swpBtnPrev${buttonSeq.current}`

  const handleResize = useCallback(() => {
    const clientWidth = document.body.clientWidth

    if (clientWidth >= 1367) {
      setItemsPerRows(5)
    } else if (clientWidth >= 1281) {
      setItemsPerRows(4)
    } else if (clientWidth >= 768) {
      setItemsPerRows(3)
    } else if (itemsPerRows !== 2) {
      setItemsPerRows(2)
    }
  }, [itemsPerRows, setItemsPerRows])

  useEffect(() => {
    handleResize()
  }, [])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])
  return data?.products.length ? (
    <section className='pb-4' style={customStyle}>
      <div
        className={`flash-sale--header relative flex flex-col justify-center px-5 py-4 ${headerClassName}`}
      >
        {!data?.customization?.backgroundImage && (
          <>
            <h2
              className=' text-headingMedium block w-full font-bold'
              style={{
                color: data?.customization?.titleColor || '#2d3748',
              }}
            >
              {getTranslate(data?.name)}
            </h2>
            <p
              className='subTitle text-mainBody text-black-400'
              style={{
                color: data?.customization?.descriptionColor || '#000000',
              }}
            >
              {getTranslate(data?.description)}
            </p>
          </>
        )}

        {!!(data as FlashSale)?.endTime && showCountdown && (
          <FlashSaleProductCountdown
            endTime={(data as FlashSale)?.endTime || 0}
            className='absolute right-4 top-1/2 w-fit -translate-y-1/2'
            size='large'
            light
          />
        )}
      </div>

      <div className='productCarousel--content group relative mt-16 px-5'>
        <Swiper
          modules={[Grid, FreeMode, Autoplay, Navigation]}
          grid={{ rows: rows, fill: 'row' }}
          spaceBetween={12}
          autoplay={{ delay: 5000 }}
          speed={400}
          freeMode
          navigation={{
            nextEl: `.${nextBtnClassName}`,
            prevEl: `.${prevBtnClassName}`,
          }}
          slidesPerView={itemsPerRows}
        >
          {sortDataPC?.map((product: Product, idx: number) => (
            <SwiperSlide key={product._id + idx + 'fs'}>
              <ProductCard
                product={product}
                className='productCardSwp'
                hideFlashSaleCountdown={showCountdown}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          className={`swpNav ${prevBtnClassName} stroke-primary-dark absolute left-0 top-1/2 z-30 flex h-[36px] w-[36px] translate-y-[-50%] cursor-pointer items-center opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
        >
          <img
            src='/assets/icons/arrow/slider-button.svg'
            alt='Prev'
            className='h-full w-full rotate-180 bg-full'
          />
        </div>
        <div
          className={`swpNav ${nextBtnClassName} stroke-primary-dark absolute right-0 top-1/2 z-30 flex h-[36px] w-[36px] translate-y-[-50%] cursor-pointer items-center opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
        >
          <img
            src='/assets/icons/arrow/slider-button.svg'
            alt='Next'
            className='h-full w-full bg-full'
          />
        </div>
      </div>

      {!hideViewAll && (
        <Link
          href={data?.customization?.viewMoreLink || '/'}
          className='mx-auto mt-3 flex h-12 w-[210px] items-center justify-center rounded-sm bg-white text-link'
          style={{
            backgroundColor: data?.customization?.viewMoreBackgroundColor,
            color: data?.customization?.viewMoreLink,
          }}
        >
          Xem tất cả &gt;&gt;&gt;
        </Link>
      )}
    </section>
  ) : null
}
export default ProductCarousel
